// Dashboard: iş izinleri durum grafiği (Tümü / Haftalık / Aylık) ve "Bugün Sahada" özeti — tek izin listesinden
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ClipboardCheck, ArrowRight, HardHat, Users, Truck } from 'lucide-react';
import { ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { iso } from '../settings/calendarViews';
import { phaseOf } from '../work-permits/TodayOnSite';
import { isPermitExpired } from '../work-permits/constants';

const approvedRaw = (p) => p.status === 'aktif' || p.status === 'onaylandi';
const GROUPS = [
  ['Onaylandı', '#10b981', p => approvedRaw(p) && !isPermitExpired(p)],
  ['Süresi Dolmuş', '#a16207', p => approvedRaw(p) && isPermitExpired(p)],
  ['Eksik/Hatalı', '#f59e0b', p => p.status === 'eksik_hatali' || p.status === 'talep_bilgisi_hatali'],
  ['Taslak', '#94a3b8', p => p.status === 'taslak'],
  ['Red', '#ef4444', p => p.status === 'red_edildi' || p.status === 'reddedildi'],
  ['İptal', '#6b7280', p => p.status === 'iptal' || p.status === 'geri_cekildi'],
  ['Tamamlandı', '#0f766e', p => p.status === 'tamamlandi'],
];
const known = (p) => GROUPS.some(([, , f]) => f(p));
export const matchesGroup = (name, p) => name === 'Bekleyen' ? !known(p) : !!GROUPS.find(([n]) => n === name)?.[2](p);
// İşlemi süren (kapanmamış) talepler: Onaylandı + Eksik/Hatalı + Bekleyen
export const isOngoing = (p) => ['Onaylandı', 'Eksik/Hatalı', 'Bekleyen'].some(n => matchesGroup(n, p));

export const periodRange = (period, now = new Date()) => {
  if (period === 'week') {
    const d = new Date(now); d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const e = new Date(d); e.setDate(d.getDate() + 6);
    return [iso(d), iso(e)];
  }
  if (period === 'month') return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(new Date(now.getFullYear(), now.getMonth() + 1, 0))];
  return null;
};

// Dönemle kesişen (başlangıç–bitiş aralığı haftaya/aya değen) izinler
export const inPeriodOne = (p, period, r = periodRange(period)) => !r || (!!p.start_date && p.start_date <= r[1] && (p.end_date || p.start_date) >= r[0]);
export const inPeriod = (permits, period) => {
  const r = periodRange(period);
  return r ? permits.filter(p => inPeriodOne(p, period, r)) : permits;
};

export const isApproved = approvedRaw;

export const PERIODS = [['all', 'Tümü'], ['week', 'Haftalık'], ['month', 'Aylık']];
export const PERIOD_LABELS = { week: 'Bu hafta', month: 'Bu ay' };

export const PermitStatusChart = ({ stats, period, setPeriod, onDetail, onBarClick }) => {
  // Sayılar sunucuda (aynı gruplama kuralıyla) hesaplanır
  const cur = stats?.[period] || { total: 0, groups: {} };
  const list = { length: cur.total };
  const order = [...GROUPS.slice(0, 4).map(([n, f]) => [n, f]), ['Bekleyen', '#3b82f6'], ...GROUPS.slice(4).map(([n, f]) => [n, f])];
  const data = order.map(([name, fill]) => ({ name, fill, value: cur.groups[name] || 0 }));
  return (
    <Card className="border-slate-200" data-testid="dashboard-permit-chart">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center justify-between gap-3 text-lg">
          <span className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5" />İş İzinleri Durumu <span className="text-sm font-normal text-slate-400" data-testid="dashboard-chart-total">({list.length} izin)</span></span>
          <span className="flex items-center gap-3">
            <span className="flex rounded-lg bg-slate-100 p-1" data-testid="dashboard-period-toggle">
              {PERIODS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setPeriod(k)} aria-pressed={period === k} data-testid={`dashboard-period-${k}`}
                  className={`rounded-md px-3 py-1 text-sm transition-colors ${period === k ? 'bg-orange-500 font-semibold text-white shadow-sm' : 'text-slate-600 hover:bg-white hover:text-slate-900'}`}>{l}</button>
              ))}
            </span>
            <button onClick={onDetail} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-testid="dashboard-chart-detail">Detay <ArrowRight className="h-4 w-4" /></button>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {list.length ? (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical" accessibilityLayer={false} style={{ outline: "none" }} margin={{ top: 5, right: 20, left: 100, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={110} />
                <Tooltip cursor={false} separator="" formatter={(v) => [`${v} izin`, '']} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} cursor="pointer" onClick={(d) => d?.name && onBarClick?.(d.name)} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex h-60 items-center justify-center text-slate-400">
            <div className="text-center"><ClipboardCheck className="mx-auto mb-2 h-12 w-12 opacity-50" /><p className="text-sm">{period === 'all' ? 'İzin verisi yok' : 'Bu dönemde izin yok'}</p></div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const TodayOnSiteCard = ({ list, onClick }) => {
  // list: sunucudan gelen, bugüne denk gelen onaylı talepler (hafif alanlar); saat aşaması burada canlı hesaplanır
  const now = new Date();
  const today = iso(now);
  const mins = now.getHours() * 60 + now.getMinutes();
  const people = new Set(list.flatMap(p => p.employee_ids || [])).size;
  const equipment = new Set(list.flatMap(p => p.equipment_ids || [])).size;
  const running = list.filter(p => phaseOf(p, today, mins) === 'run').length;
  const Item = ({ icon: Icon, value, label, testid }) => (
    <div className="flex items-center gap-2" data-testid={testid}><Icon className="h-4 w-4 text-emerald-600" /><span className="text-xl font-bold text-slate-900">{value}</span><span className="text-sm text-slate-500">{label}</span></div>
  );
  return (
    <Card className="cursor-pointer border-emerald-200 transition-shadow hover:shadow-md" onClick={onClick} data-testid="dashboard-today-onsite">
      <CardContent className="flex flex-wrap items-center gap-x-8 gap-y-3 p-5">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-emerald-500 p-2.5"><HardHat className="h-5 w-5 text-white" /></div>
          <div><p className="font-semibold text-slate-900">Bugün Sahada</p><p className="text-xs text-slate-500" data-testid="dashboard-today-running">{running} iş şu an devam ediyor</p></div>
        </div>
        <Item icon={ClipboardCheck} value={list.length} label="talep" testid="dashboard-today-permits" />
        <Item icon={Users} value={people} label="kişi" testid="dashboard-today-people" />
        <Item icon={Truck} value={equipment} label="ekipman" testid="dashboard-today-equipment" />
        <span className="ml-auto flex items-center gap-1 text-sm text-emerald-700">Takvimde gör <ArrowRight className="h-4 w-4" /></span>
      </CardContent>
    </Card>
  );
};
